import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { editWorkItemStyles } from '../styles';
import { supportedWorkItemUserFieldValues, useUserFieldHook } from './use-user-field-hook';
import { useAutomationRuleActions } from '../../use-actions';
import { ActionItemType, EditWorkItemActionType } from '../../types';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';

interface Props {
  id?: string;
  actionId: string;
  validationError: string | null;
	isMultiSelect?: boolean;
}

const clearTimeoutHandler = (debounce: NodeJS.Timeout | null) => {
	if (debounce) {
		clearTimeout(debounce);
	}
};

export default function UserFieldInput(props: Props) {
	const classes = editWorkItemStyles();
	const userFieldHook = useUserFieldHook();
	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();
  
	let debounce: NodeJS.Timeout | null = null;

	const [loading, setLoading] = useState(false);
	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [initialOptions, setInitialOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: EditWorkItemActionType = useMemo(() => {
		const foundActions = actions.find(item => item.id === props.actionId) as ActionItemType;
		return foundActions!.editWorkItem as EditWorkItemActionType;
	}, [actions, props.actionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		clearTimeoutHandler(debounce);
	
		if (value === '') {
			userFieldHook.getIdentityOptions().then((resultOptions) => {
				setOptions([
					...resultOptions,
					...supportedWorkItemUserFieldValues
				]);
				setLoading(false);
			});
		}
		
		if (value !== '') {
			setLoading(true);
			debounce = setTimeout(() => {
				userFieldHook.searchIdentityOptions(value)
					.then((resultOptions) => {
						setOptions([
							...resultOptions,
						]);

						const itIsNotInInitialOptions: Array<{label: string; value: string}> = resultOptions.filter((resultOption) => {
							return !initialOptions.some(initialOptions => initialOptions.value === resultOption.value);
						});

						setInitialOptions((oldInitialOptions) => [...oldInitialOptions, ...itIsNotInInitialOptions]);
					})
					.finally(() => {
						setLoading(false);
					});
			}, 750);
		}
	}, [initialOptions, options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundAction = actions.find(item => item.id === props.actionId);

			if (props.isMultiSelect) {
				setStatus('searching');
				setText('');
			}

			if (!props.isMultiSelect) {
				setStatus('selected');
			}
	
			if (foundAction) {
				if (props.isMultiSelect) {
					setActions(oldActions => {
						const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
						const others = oldActions.filter(item => item.id !== props.actionId);

						const selectedValues = current.editWorkItem!.userFieldValue.includes(value) ?
							current.editWorkItem!.userFieldValue.filter(item => item !== value) :
							[...current.editWorkItem!.userFieldValue, value];

						return [...others, {
							...current, editWorkItem: {
								...current.editWorkItem as EditWorkItemActionType,
								userFieldValue: selectedValues
							}
						}];
					});
				}

				if (!props.isMultiSelect) {
					setActions(oldActions => {
						const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
						const others = oldActions.filter(item => item.id !== props.actionId);
		
						return [...others, {
							...current, editWorkItem: {
								...current.editWorkItem as EditWorkItemActionType,
								userFieldValue: [value]
							}
						}];
					});
				}
			}
		}
	}, [
		props.isMultiSelect,
		initialOptions
	]);

	const clearOptionSelect = useCallback(() => {
		const foundAction = actions.find(item => item.id === props.actionId);
		setStatus('selected');

		if (foundAction) {
			setActions(oldActions => {
				const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
				const others = oldActions.filter(item => item.id !== props.actionId);

				return [...others, {
					...current, editWorkItem: {
						...current.editWorkItem as EditWorkItemActionType,
						userFieldValue: []
					}
				}];
			});
		}
	}, []);

	const loadInitialOptions = useCallback(async () => {
		if (initialOptions.length > 0) {
			setOptions(initialOptions);
		}

		if (!initialOptions.length) {
			userFieldHook.getIdentityOptions().then((resultOptions) => {
				const list = [
					...resultOptions,
					...supportedWorkItemUserFieldValues
				];
	
				setInitialOptions(list);
				setOptions(list);
			});
		}
	}, [initialOptions]);
  
	const valueRender = useMemo(() => {
		if (current && current.userFieldValue && status === 'selected') {
			return options.filter((item) => current.userFieldValue.includes(item.value)).map((item) => item.label).join(', ');
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current, options, status]);

	const selectedOptions = useMemo(() => {
		const filteredValues = options.filter(item => current.userFieldValue.includes(item.value));

		if (current.userFieldValue && filteredValues.length > 0) {
			return filteredValues.map(item => item.value);
		}

		return [];
	}, [current, options]);

	useEffect(() => {
		setOnInitialLoading(true);
		const foundAction = actions.find(item => item.id === props.actionId);
		const asyncHandler = async () => {
			if(
				foundAction && 
				foundAction?.editWorkItem && 
				foundAction?.editWorkItem?.inputValueType === 'user-field' &&
				!foundAction.editWorkItem.userFieldValue
			) {
				await loadInitialOptions();
			}
	
			if (
				foundAction && 
				foundAction?.editWorkItem && 
				foundAction?.editWorkItem?.inputValueType === 'user-field' &&
				foundAction.editWorkItem.userFieldValue
			) {
				const resultOptions = await userFieldHook.getIdentityOptions();
				const list = [
					...resultOptions,
					...supportedWorkItemUserFieldValues
				];
				
				await Promise.allSettled(
					foundAction
						.editWorkItem.userFieldValue
						.filter(item => !supportedWorkItemUserFieldValues.some(supportedItem => supportedItem.value === item))
						.map(async (item) => {
							const result = await userFieldHook.getIdentityById(item);
							if (list.every(item => item.value !== result.value) && result) {
								list.push(result);
							}
						})
				);

				setOptions(list);
			}

			setOnInitialLoading(false);
		};

		asyncHandler();
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable={!props.isMultiSelect}
				clearIcon={(
					<ClearebleButtonInput onClick={clearOptionSelect} />
				)}
				inputMode='search'
				autoComplete='off'
				multiselect={props.isMultiSelect}
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onFocus={(ev) => {
					ev.preventDefault();
					setStatus('searching');
					setText('');
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					loadInitialOptions();
				}}
				multiple
			>
				<div style={{maxHeight: 240}}>
					{!loading && options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
					{loading && <Option value='loading'>Loading...</Option>}
				</div>
			</Combobox>
		</Field>
	);
}